import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Pencil, Archive, Trash2, FileText, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { clientsApi, operationsApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { EXPERT_CLIENT_VIEW_LIMIT } from "@/lib/permissions";
import { sendIntrusionAlert } from "@/lib/security";
import { logClientView } from "@/lib/activity";

const typeLabel = {
  DEPOT: "Dépôt",
  RESTITUTION: "Restitution",
  DELIVRANCE: "Délivrance",
};

const fmt = (d) => {
  try {
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function ClientDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Memoise the formatted address — the underlying client is only set after
  // the network round-trip so we don't want React to re-build the string on
  // unrelated re-renders (e.g. when operations update).
  const formattedAddress = useMemo(() => {
    if (!client) return "";
    return [client.adresse1, client.adresse2, client.code_postal, client.ville, client.pays]
      .filter(Boolean)
      .join(", ");
  }, [client]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, ops] = await Promise.all([
        clientsApi.get(id),
        operationsApi.list({ client_id: id }),
      ]);
      setClient(c);
      setOperations(ops);
    } catch (err) {
      toast.error("Client introuvable");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Silent monitoring: count distinct client file views by experts.
  // After EXPERT_CLIENT_VIEW_LIMIT distinct files in the session, send one
  // discreet email alert to the admin. No UI disruption for the expert.
  // Also logs every non-admin view for the monthly recap.
  useEffect(() => {
    if (!user || !client) return;
    // Log every non-admin view for the monthly recap (silent)
    logClientView(user, client);

    if (user.role !== "expert" || !id) return;
    const KEY_SET = "vidoc_expert_viewed_clients";
    const KEY_NOTIFIED = "vidoc_expert_alert_sent";
    try {
      const viewed = new Set(JSON.parse(sessionStorage.getItem(KEY_SET) || "[]"));
      if (viewed.has(id)) return;
      viewed.add(id);
      sessionStorage.setItem(KEY_SET, JSON.stringify(Array.from(viewed)));

      if (
        viewed.size >= EXPERT_CLIENT_VIEW_LIMIT &&
        sessionStorage.getItem(KEY_NOTIFIED) !== "1"
      ) {
        sessionStorage.setItem(KEY_NOTIFIED, "1");
        sendIntrusionAlert({
          username: user.username,
          role: user.role,
          actionCount: viewed.size,
          description: `Expert a consulté ${viewed.size} fiches clients distinctes dans la même session (seuil : ${EXPERT_CLIENT_VIEW_LIMIT}).`,
        });
      }
    } catch (e) {
      // Silent monitoring should never break the UI, but log so the
      // expert-watch incident loss is traceable in dev tools.
      console.warn("[ClientDetail] expert-view monitoring failed", e?.name || e?.message);
    }
    // `EXPERT_CLIENT_VIEW_LIMIT`, `KEY_SET`, `KEY_NOTIFIED`, `logClientView`
    // and `sendIntrusionAlert` are module-level constants/imports — they
    // never change. `id`, `user` and `client` are the only meaningful deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, client]);

  const handleArchive = async () => {
    if (!window.confirm(`Archiver ${client.nom} ?`)) return;
    setBusy(true);
    try {
      await clientsApi.remove(id);
      toast.success("Client archivé");
      navigate("/clients");
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const handleDeletePermanent = async () => {
    if (
      !window.confirm(
        `Suppression DÉFINITIVE de ${client.nom} ? Cette action est irréversible.`
      )
    )
      return;
    setBusy(true);
    try {
      await clientsApi.removePermanent(id);
      toast.success("Client supprimé définitivement");
      navigate("/clients");
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !client) {
    return <div className="p-8 text-[var(--vidoc-text-secondary)]">Chargement...</div>;
  }

  return (
    <div data-testid="client-detail-page">
      <PageHeader
        title={`${client.nom}${client.prenom ? " " + client.prenom : ""}`}
        subtitle={
          [
            client.numero_client && `N° ${client.numero_client}`,
            client.ville,
            client.archived && "ARCHIVÉ",
          ]
            .filter(Boolean)
            .join(" · ") || " "
        }
        backTo="/clients"
        right={
          <button
            data-testid="client-edit-btn"
            onClick={() => navigate(`/clients/${id}/edit`)}
            className="p-2 rounded-full hover:bg-[var(--vidoc-bg-secondary)] touch-target"
          >
            <Pencil className="w-5 h-5" />
          </button>
        }
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-6">
        <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)]">
          <Info label="Email" value={client.email} />
          <Info label="Téléphone" value={client.telephone} />
          <Info
            label="Adresse"
            value={formattedAddress}
          />
          {client.frais_vente > 0 && <Info label="Frais de vente" value={`${client.frais_vente}%`} />}
          {client.iban && <Info label="IBAN" value={client.iban} />}
          {client.observations && <Info label="Observations" value={client.observations} preWrap />}
        </div>

        <section>
          <h2 className="text-sm font-semibold mb-2 flex items-center justify-between">
            <span>Opérations ({operations.length})</span>
          </h2>
          {operations.length === 0 ? (
            <div className="bg-white rounded-xl border border-[var(--vidoc-border)] p-6 text-center text-sm text-[var(--vidoc-text-secondary)]">
              Aucune opération pour ce client.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
              {operations.map((op) => (
                <Link
                  key={op.id}
                  to={`/operations/${op.id}`}
                  data-testid={`op-row-${op.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--vidoc-bg-secondary)]"
                >
                  <FileText className="w-5 h-5 text-[var(--vidoc-primary)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">
                      {typeLabel[op.type] || op.type} ·{" "}
                      <span className="text-xs text-[var(--vidoc-text-secondary)]">
                        N° {op.id}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--vidoc-text-secondary)]">
                      {fmt(op.date)} · {op.media_ids?.length || 0} média(s)
                      {op.email_sent && " · ✉️ envoyé"}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--vidoc-text-tertiary)]" />
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Action bar : Archive + Permanent delete for active clients; only Permanent delete for archived */}
        {(!client.archived || user?.role === "admin") && (
          <div className="flex gap-2">
            {!client.archived && (
              <button
                data-testid="client-archive-btn"
                onClick={handleArchive}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)] touch-target text-sm font-medium"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                Archiver
              </button>
            )}
            {user?.role === "admin" && (
              <button
                data-testid="client-delete-permanent-btn"
                onClick={handleDeletePermanent}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[var(--vidoc-danger)] hover:bg-red-50 border border-[var(--vidoc-danger)] touch-target text-sm font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer définitivement
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, preWrap }) {
  if (!value) return null;
  return (
    <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:gap-4">
      <div className="text-xs font-medium text-[var(--vidoc-text-secondary)] sm:w-32">{label}</div>
      <div className={`text-sm flex-1 ${preWrap ? "whitespace-pre-wrap" : ""}`}>{value}</div>
    </div>
  );
}
